import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Shield, ShieldAlert, Laptop, Network, Server, Globe, HelpCircle, Router,
  RefreshCw, Pencil, Check, X, PauseCircle, PlayCircle, Trash2, AlertTriangle,
} from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';
import { useAuth } from '../context/AuthContext';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import TenantSelect from '../components/TenantSelect';

interface LogSource {
  id: number;
  broker_id: number;
  broker_name: string;
  site_name: string;
  tenant_name: string;
  sender_ip: string;
  hostname: string;
  vendor: string | null;
  product: string | null;
  effective_device_type: string;
  device_type_override: string | null;
  label: string | null;
  formats: Record<string, number>;
  event_count: string;
  first_seen_at: string;
  last_seen_at: string;
  silent_minutes: string | null;
  open_issues: number;
  suspended: boolean;
}

const DEVICE_TYPES = ['firewall', 'ips', 'endpoint', 'network', 'server', 'proxy', 'dhcp', 'unknown'];

const LOG_SOURCE_COLUMNS: ColumnDef[] = [
  { key: 'type', label: 'Type', defaultVisible: true },
  { key: 'device', label: 'Device', defaultVisible: true },
  { key: 'sender_ip', label: 'Sender IP', defaultVisible: true },
  { key: 'vendor', label: 'Vendor / product', defaultVisible: true },
  { key: 'formats', label: 'Formats', defaultVisible: true },
  { key: 'events', label: 'Events', defaultVisible: true },
  { key: 'last_log', label: 'Last log', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'actions', label: 'Actions', defaultVisible: true },
];

const TYPE_ICON: Record<string, typeof Shield> = {
  firewall: Shield, ips: ShieldAlert, endpoint: Laptop,
  network: Network, server: Server, proxy: Globe, dhcp: Router, unknown: HelpCircle,
};

const TYPE_STYLE: Record<string, string> = {
  firewall: 'bg-blue-100 text-blue-700',
  ips: 'bg-red-100 text-red-700',
  endpoint: 'bg-violet-100 text-violet-700',
  network: 'bg-cyan-100 text-cyan-700',
  server: 'bg-emerald-100 text-emerald-700',
  proxy: 'bg-amber-100 text-amber-700',
  dhcp: 'bg-indigo-100 text-indigo-700',
  unknown: 'bg-gray-100 text-gray-500',
};

function formatAgo(minutes: string | null): string {
  if (minutes === null) return 'never';
  const value = Math.round(Number(minutes));
  if (value < 1) return 'just now';
  if (value < 60) return `${value} min ago`;
  const hours = Math.floor(value / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// Apparati che stanno inviando log, riconosciuti per IP mittente e hostname: uno stesso broker
// può raccogliere da firewall, EDR, switch e server diversi.
export default function LogSourcesPage() {
  const { isAdmin } = useAuth();
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.log_sources', LOG_SOURCE_COLUMNS);
  const [sources, setSources] = useState<LogSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editType, setEditType] = useState('unknown');
  const [deletingSource, setDeletingSource] = useState<LogSource | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    const res = await axios.get('/api/log-sources', { params: { tenantId: selectedTenantId ?? undefined } });
    setSources(res.data.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  function startEdit(source: LogSource) {
    setEditingId(source.id);
    setEditLabel(source.label || '');
    setEditType(source.effective_device_type);
  }

  async function save(id: number) {
    await axios.put(`/api/log-sources/${id}`, { deviceType: editType, label: editLabel });
    setEditingId(null);
    load();
  }

  async function toggleSuspend(source: LogSource) {
    await axios.put(`/api/log-sources/${source.id}/suspend`, { suspended: !source.suspended });
    load();
  }

  async function confirmDelete(deleteLogs: boolean) {
    if (!deletingSource) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/log-sources/${deletingSource.id}`, { params: { deleteLogs } });
      setDeletingSource(null);
      load();
    } finally {
      setDeleting(false);
    }
  }

  const byBroker = sources.reduce<Record<string, LogSource[]>>((acc, s) => {
    const key = `${s.tenant_name} · ${s.site_name} · ${s.broker_name}`;
    (acc[key] ||= []).push(s);
    return acc;
  }, {});

  function renderCell(s: LogSource, key: string) {
    const editing = editingId === s.id;
    switch (key) {
      case 'type': {
        const Icon = TYPE_ICON[s.effective_device_type] || HelpCircle;
        return editing ? (
          <select value={editType} onChange={e => setEditType(e.target.value)} className="input-field py-1 text-xs">
            {DEVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        ) : (
          <span className={`badge inline-flex items-center gap-1 ${TYPE_STYLE[s.effective_device_type] || TYPE_STYLE.unknown}`}>
            <Icon size={11} /> {s.effective_device_type}
            {s.device_type_override && <span title="Set manually">*</span>}
          </span>
        );
      }
      case 'device':
        return editing ? (
          <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
            placeholder={s.hostname} className="input-field py-1 text-xs w-48" />
        ) : (
          <>
            {s.label || s.hostname}
            {s.label && <span className="text-gray-400 font-normal"> · {s.hostname}</span>}
          </>
        );
      case 'sender_ip':
        return s.sender_ip;
      case 'vendor':
        return [s.vendor, s.product].filter(Boolean).join(' ') || '—';
      case 'formats':
        return Object.entries(s.formats || {}).map(([format, count]) => (
          <span key={format} className="badge bg-gray-100 text-gray-600 mr-1">{format} · {count}</span>
        ));
      case 'events':
        return Number(s.event_count).toLocaleString('en-GB');
      case 'last_log':
        return formatAgo(s.silent_minutes);
      case 'status':
        return s.suspended
          ? <span className="badge bg-gray-200 text-gray-600">Suspended</span>
          : <span className="badge bg-green-100 text-green-700">Active</span>;
      case 'actions':
        return editing ? (
          <span className="inline-flex gap-1">
            <button onClick={() => save(s.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
            <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
          </span>
        ) : !isAdmin ? <span className="text-gray-300">—</span> : (
          <span className="inline-flex gap-1">
            <button onClick={() => startEdit(s)} title="Edit" className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded">
              <Pencil size={13} />
            </button>
            <button onClick={() => toggleSuspend(s)} title={s.suspended ? 'Resume ingestion' : 'Suspend ingestion'}
              className="p-1 text-gray-400 hover:text-amber-600 hover:bg-gray-50 rounded">
              {s.suspended ? <PlayCircle size={13} /> : <PauseCircle size={13} />}
            </button>
            <button onClick={() => setDeletingSource(s)} title="Delete" className="p-1 text-gray-400 hover:text-red-600 hover:bg-gray-50 rounded">
              <Trash2 size={13} />
            </button>
          </span>
        );
      default:
        return null;
    }
  }

  const CELL_CLASS: Record<string, string> = {
    device: 'font-medium text-gray-800',
    sender_ip: 'text-gray-500 font-mono text-xs',
    vendor: 'text-gray-500',
    events: 'text-gray-800',
    actions: 'text-right',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Log Sources</h1>
          <TenantSelect />
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-1">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Devices sending logs, identified by sender IP and declared hostname. The device type is inferred
        from the log content and can be corrected manually.
      </p>

      {loading && sources.length === 0 && <div className="card p-4 text-gray-400 text-sm">Loading…</div>}

      {!loading && sources.length === 0 && (
        <div className="card p-8 text-center">
          <HelpCircle size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No device has sent logs yet on the current scope.</p>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(byBroker).map(([brokerKey, list]) => (
          <div key={brokerKey} className="card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <h2 className="font-medium text-gray-900 text-sm">{brokerKey}</h2>
              <span className="badge bg-gray-100 text-gray-600">{list.length} device(s)</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {orderedDefs.map(d => (
                    <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn}
                      className={`table-header ${d.key === 'actions' ? 'text-right' : ''}`}>
                      {d.label}
                    </DraggableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map(s => (
                  <tr key={s.id} className={`border-t border-gray-100 ${s.suspended ? 'opacity-50' : s.open_issues > 0 ? 'bg-amber-50/60' : ''}`}>
                    {orderedDefs.map(d => (
                      <td key={d.key}
                        className={`table-cell ${CELL_CLASS[d.key] || ''} ${d.key === 'last_log' ? (s.open_issues > 0 ? 'text-amber-700 font-medium' : 'text-gray-500') : ''}`}
                        title={d.key === 'last_log' ? new Date(s.last_seen_at).toLocaleString('en-GB') : undefined}>
                        {renderCell(s, d.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {deletingSource && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-500" /> Delete device
              </h2>
              <button onClick={() => setDeletingSource(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>
                Remove <span className="font-medium">{deletingSource.label || deletingSource.hostname}</span> from
                the device inventory. What should happen to the logs already collected from it?
              </p>
            </div>
            <div className="flex flex-col gap-2 px-5 pb-5">
              <button onClick={() => confirmDelete(false)} disabled={deleting} className="btn-secondary w-full">
                Delete device, keep collected logs
              </button>
              <button onClick={() => confirmDelete(true)} disabled={deleting}
                className="w-full px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                Delete device and collected logs
              </button>
              <button onClick={() => setDeletingSource(null)} disabled={deleting} className="text-xs text-gray-400 hover:text-gray-600 mt-1">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
